import { AccountModelAccessor, SpaceModelAccessor } from '@mode/legacy/data-access-model-js';
import { ModeResourceHelper } from '@mode/shared/util-halogen';
import { HomeActionType } from '@mode/legacy/util-domain';
import { uniqBy } from 'lodash';
import { AsyncAction } from 'redux-promise-middleware';
import { BugsnagHelpers } from '~/lib/bugsnag-helpers';
import { SpaceSearchService } from '../services/space-search-service';

export { HomeActionType };

export type HomeViewType =
  | 'datasource'
  | 'discover'
  | 'exploreReport'
  | 'myexplorations'
  | 'mywork'
  | 'recent'
  | 'space'
  | 'spaceLoading'
  | 'spacesIndex'
  | 'starred';

export type HomeViewPreferenceType =
  | 'discover'
  | 'explorations'
  | 'reports'
  | 'search'
  | 'starred'
  | 'organization_space'
  | '';

export interface SelectedSpace {
  token: string;
  name: HomeViewType;
  title: string;
  subTitle: string;
  preferenceView: HomeViewPreferenceType;
  space: any;
  datasourceToken?: string;
  allSpaces: any[];
  statusCode: number | null;
}

export interface ReportSearchState {
  // TODO (Franklin) 04/07/2020: Add query options typing for report indexes
  // forthcomingSearchOptions: Partial<QueryOptions<'reportLastEdited'>>
  //   | Partial<QueryOptions<'reportAlphabetical'>>
  //   | Partial<QueryOptions<'reportAlphabeticalDesc'>>;
  forthcomingSearchOptions: Object;
}

export interface ReportSidePanelState {
  reportSidePanelOpen: boolean;
  reportSidePanelReport: any;
  reportSidePanelQueries: any[];
}

export interface DefinitionState {
  definitionList: any[];
  definitionListLoading: boolean;
  nextPage: any;
}

export interface HomeState {
  selectedSpace?: SelectedSpace;
  selectedReport?: any;
  reportListToggle?: 'list' | 'tile';
  reportSearch?: ReportSearchState;
  reportState?: any;
  reportSidePanelState?: any;
  definitionState?: any;
  mySpaces?: any[];
  totalSpaceCount?: Number;
  totalDataSourceCount?: Number;
}

export interface ReportState {
  reportList?: any[];
  nextPage?: null;
  searchOptions?: any;
  reportListLoading?: boolean;
  noReports?: boolean;
  hasArchived?: boolean;
  reportsCollection?: any;
}

export interface ReportListAction extends AsyncAction {
  meta: {
    options?: any;
    reportListDetails: {
      name: string;
      token?: string;
    };
  };
}

const defaultReportListIncludes = {
  reports: ['creator', 'star', 'new_star', 'new_space_link', { space: 'creator' }],
};

export const selectStarred = () => ({
  type: HomeActionType.SelectStarred,
});

export const selectMyWork = () => ({
  type: HomeActionType.SelectMyWork,
});

export const selectDiscover = () => ({
  type: HomeActionType.SelectDiscover,
});

export const selectMyExplorations = () => ({
  type: HomeActionType.SelectExplorations,
});

export const selectSpacePending = () => ({
  type: HomeActionType.SelectSpacePending,
});

export const selectSpaceRejected = (err: Error) => ({
  type: HomeActionType.SelectSpaceRejected,
  payload: err,
});

export const selectSpaceFulfilled = (space: any) => ({
  type: HomeActionType.SelectSpaceFulfilled,
  payload: space,
});

export const selectReportExplore = () => ({
  type: HomeActionType.SelectReportExplore,
});

export const selectDefinitions = (dataSourceToken: string) => ({
  type: HomeActionType.SelectDefinitions,
  dataSourceToken,
});

export const selectRecent = () => ({
  type: HomeActionType.SelectRecent,
});

export const selectSpacesIndex = (
  ownerId: string,
  AccountModel: AccountModelAccessor,
  ModeCollection: any,
  SpaceModel: SpaceModelAccessor
) => ({
  type: HomeActionType.SelectSpacesIndex,
  payload: AccountModel.find({ username: ownerId, useEntryPath: true }).then((account) => {
    if (account.user) {
      throw new Error('user account spaces do not exist');
    }
    return account
      .customSpaces({ refresh: true, includes: { spaces: ['creator', 'detail'] } })
      .then(ModeCollection.elements)
      .then((spaces) => SpaceModel.buildSpacesWithCreatorAndDetail(spaces))
      .then((spaces) => ({
        spaces: spaces,
        statusCode: 200, // we're making an reasonable assumption here that bc we're further into the promise chain, that the response was 200
      }));
  }),
});

export const selectSpacesIndexPaginated = () => ({
  type: HomeActionType.SelectSpacesIndexPaginated,
});

export const addSpaceMembership = (
  space: any,
  token: string,
  SpaceMembershipModel: any,
  SpaceModel: any,
  AlertService: any,
  callback: Function,
  showToast: boolean
) => ({
  type: HomeActionType.CreateSpaceMembership,
  payload: SpaceMembershipModel.create({
    space: space.token,
    params: {
      'membership.member_token': token,
      includes: ['member'],
    },
  })
    .then((newMembership) => {
      SpaceModel.refresh(space, { includes: 'user_space_membership' });
      if (showToast) {
        AlertService.success(`Added to collection`);
      }
      callback(space);
      return newMembership;
    })
    .catch((e) => {
      console.error(e);
      BugsnagHelpers.notifySafely(e);
      AlertService.error('Error adding user');
    }),
});

export const getSpaceReports = (searchService: SpaceSearchService, owner: any, space: any) => {
  const accountFilter = owner.user ? 'user_' + owner.token : 'organization_' + owner.token;
  // do special search for my work
  const options = {
    query: '',
    clearCache: true,
    indexes: ['reportLastEdited'],
    filters: `space_${space.token},${accountFilter}`,
    perPage: 24,
    rawResults: true,
    facets: {
      reportLastEdited: 'archived:false',
    },
  };

  const archivedOptions = {
    query: '',
    clearCache: true,
    indexes: ['reportLastEdited'],
    filters: `space_${space.token},${accountFilter}`,
    perPage: 1,
    facets: {
      reportLastEdited: 'archived:true',
    },
  };

  return {
    type: HomeActionType.GetReportList,
    meta: {
      options,
      reportListDetails: {
        name: 'space',
        token: space.token,
      },
    },
    payload: searchService
      .querySpaceAndPinnedReports(options, space, true)
      .then(searchService.handleSearchResponse)
      .then(searchService.handleArchivedResponse.bind(this, archivedOptions)),
  };
};

function includeReportsCollection(reportsCollection) {
  return reportsCollection.elements().then((reports) => ({ reports, reportsCollection }));
}

export const getOwnerStarred = (account: any): ReportListAction => ({
  type: HomeActionType.GetReportList,
  meta: {
    reportListDetails: { name: 'starred' },
  },
  payload: account
    .starredReports({ includes: defaultReportListIncludes, refresh: true })
    .then(includeReportsCollection.bind(this)),
});

export const getOwnerStarredWithDatasets = (account: any): ReportListAction => ({
  type: HomeActionType.GetReportList,
  meta: {
    reportListDetails: { name: 'starred' },
  },
  payload: account
    .starredBaseReports({ includes: defaultReportListIncludes, refresh: true })
    .then(includeReportsCollection.bind(this)),
});

export const getRecentReports = (account: any): ReportListAction => ({
  type: HomeActionType.GetReportList,
  meta: {
    reportListDetails: { name: 'recent' },
  },
  payload: account
    .viewedReports({ includes: defaultReportListIncludes, refresh: true })
    .then(includeReportsCollection.bind(this)),
});

export const getRecentReportsWithDatasets = (account: any): ReportListAction => ({
  type: HomeActionType.GetReportList,
  meta: {
    reportListDetails: { name: 'recent' },
  },
  payload: account
    .viewedBaseReports({ includes: defaultReportListIncludes, refresh: true })
    .then(includeReportsCollection.bind(this)),
});

export const getMoreReports = (reportsCollection: any, viewName: HomeViewType): ReportListAction => ({
  type: HomeActionType.MoreReportList,
  meta: {
    reportListDetails: {
      name: viewName,
    },
  },
  payload: reportsCollection
    .next({ includes: defaultReportListIncludes, refresh: true })
    .then(includeReportsCollection.bind(this)),
});

export const filterSearchReports = (
  searchService: SpaceSearchService,
  space: any,
  options: any,
  callback: any
): ReportListAction => {
  const emptyQueryString = !options.query;
  const nonArchivedReports = !!options.facets && Object.values(options.facets).includes('archived:false');
  // only merge filtered results with pins if not filtering by query
  // and not filtering archived reports
  const mergeWithPins = emptyQueryString && nonArchivedReports;

  return {
    type: HomeActionType.FilterReportList,
    meta: {
      options,
      reportListDetails: {
        name: space ? 'space' : 'mywork',
        token: space ? space.token : null,
      },
    },
    payload: searchService.querySpaceAndPinnedReports(options, space, mergeWithPins).then(callback),
  };
};

export const getMoreSearchReports = (
  searchService: SpaceSearchService,
  space: any,
  options: any,
  callback: any
): ReportListAction => ({
  type: HomeActionType.MoreReportList,
  meta: {
    options,
    reportListDetails: {
      name: space ? 'space' : 'mywork',
      token: space ? space.token : null,
    },
  },
  payload: searchService.querySpaceAndPinnedReports(options, space).then(callback),
});

export const getOwnerExplorations = (account: any) => ({
  type: HomeActionType.GetExplorations,
  payload: {},
});

export const getMySpaces = (account: any, SpaceModel) => {
  return {
    type: HomeActionType.GetSpaces,
    payload: account.spaces().then((accountSpaces) => {
      // to avoid cacheing on Mode.collection, which does not consistently respect model layers (useEntryPath: true, refresh: true) options
      // use the ModeResourceHelper.resourcesToModels to collect the spaces/collections resources and return models
      // use uniqBy 'token' to avoid having two "org spaces" in side-nav
      return uniqBy(
        ModeResourceHelper.resourcesToModels(accountSpaces._resource.embedded('spaces'), SpaceModel.model),
        'token'
      );
    }),
  };
};

export const setMySpaces = (spaces: any[]) => ({
  type: HomeActionType.SetSpaces,
  spaces,
});
export const insertSpace = (space: any) => ({
  type: HomeActionType.InsertSpace,
  space,
});
export const removeSpace = (space: any) => ({
  type: HomeActionType.RemoveSpace,
  space,
});
export const deleteSpace = (space: any) => ({
  type: HomeActionType.DeleteSpace,
  space,
});
export const createSpace = (space: any) => ({
  type: HomeActionType.CreateSpace,
  space,
});

export const setSpaceCount = (count: Number) => ({
  type: HomeActionType.SetSpaceCount,
  count,
});

export const getDataSources = (owner: any, ModeCollection: any) => ({
  type: HomeActionType.GetDataSources,
  payload: owner.dataSources().then(ModeCollection.elements),
});

export const removeSpaceReportPin = (spaceReportPin, report, success, error) => ({
  type: HomeActionType.RemoveSpaceReportPin,
  payload: spaceReportPin
    .destroy()
    .then(() => {
      success();
      return report;
    })
    .catch(error),
});

export const createSpaceReportPin = (SpaceReportPinModel, space, report, success, error) => ({
  type: HomeActionType.CreateSpaceReportPin,
  payload: SpaceReportPinModel.create({
    space: space.token,
    params: {
      'space_report_pin.report_token': report.token,
    },
  })
    .then((spaceReportPin) => {
      success();
      return { report, spaceReportPin };
    })
    .catch(error),
});

export const getDefaultMyWorkReports = (
  searchService: SpaceSearchService,
  account: any,
  owner: any
): ReportListAction => {
  const accountFilter = owner.user ? 'user_' + owner.token : 'organization_' + owner.token;
  // do special search for my work
  const options = {
    query: '',
    clearCache: true,
    indexes: ['reportLastEdited'],
    filters: `editor_${account.token},${accountFilter}`,
    perPage: 20,
    rawResults: true,
    facets: {
      reportLastEdited: 'archived:false',
    },
  };

  return {
    type: HomeActionType.GetReportList,
    meta: {
      options,
      reportListDetails: { name: 'mywork' },
    },
    payload: searchService.querySpace(options).then(searchService.handleSearchResponseWithSpaces),
  };
};

export const getDefinitions = (search: any, options: any, callback: any) => ({
  type: HomeActionType.GetDefinitionList,
  payload: search.query(options).then(callback),
});

export const getMoreDefinitions = (search: any, options: any, callback: any) => ({
  type: HomeActionType.MoreDefinitionList,
  payload: search.query(options).then(callback),
});

export const setReportListToggle = (type) => ({
  type: type === 'list' ? HomeActionType.SetReportListToggleList : HomeActionType.SetReportListToggleTile,
});

export const openReportSidePanel = () => ({
  type: HomeActionType.OpenReportSidePanel,
});

export const closeReportSidePanel = () => ({
  type: HomeActionType.CloseReportSidePanel,
});

export const setReportSidePanelReport = (token: string) => ({
  type: HomeActionType.SetReportSidePanelReport,
  token,
});

export const getReportSidePanelQueries = (report, ModeCollection: any, callback) => ({
  type: HomeActionType.GetReportSidePanelQueries,
  payload: report.queries().then(ModeCollection.elements).then(callback),
});

export const removeReportFromList = (token: string) => ({
  type: HomeActionType.RemoveReportFromList,
  token,
});

export const insertReportToList = (report) => ({
  type: HomeActionType.InsertReportToList,
  report,
});
